import React, {FormEvent, useEffect, useState} from "react";
import {ActionRow, ActionRowButton, Alert, InputLabel, Modal, TextField, useNotifications} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {PrinterIcon} from "@heroicons/react/20/solid";
import {PlacedOrderOverview} from "@/data/entities/order";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {POS_ORDER_QUERY_KEY} from "@/context/pos/UsePosOrder";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {api} from "@/data/api";

/** Employee codes are 4 characters. */
const EMPLOYEE_CODE_LENGTH = 4;

type Props = {
    show: boolean,
    setShow: (show: boolean) => void,
    order: PlacedOrderOverview,
};

/**
 * Before printing the delivery receipt, the courier enters their employee
 * code to link the order to them (later used for the tip). Printing without
 * linking is possible as well.
 */
const CourierPrintModal = ({show, setShow, order}: Props) => {
    const intl = useIntl();
    const {notify} = useNotifications();
    const {dashboardLocation} = useRestaurant();
    const queryClient = useQueryClient();
    const [code, setCode] = useState("");
    const [errorMsgId, setErrorMsgId] = useState<string | null>(null);

    const {mutate: print, isPending} = useMutation({
        mutationFn: (courierCode: string | null) => api.orders.printOrder(dashboardLocation.id, order.cartPublicId, {
            deliveryReceipt: true,
            kitchenReceipt: false,
            customerReceipt: false,
            courierCode,
        }),
        onSuccess: () => {
            notifySuccess(intl.formatMessage({id: "printJobQueued"}), notify, PrinterIcon);
            // Show the linked courier on the order page
            void queryClient.invalidateQueries({
                queryKey: [POS_ORDER_QUERY_KEY, dashboardLocation.id, order.cartPublicId],
            });
            setShow(false);
        },
        onError: (e: AxiosError) => {
            if (e.response?.status === 404) {
                // Unknown code, or an employee of another restaurant
                setErrorMsgId("invalidEmployeeCode");
                return;
            }

            notifyDanger(intl.formatMessage({id: "printJobFailed"}), notify);
        },
    });

    // Start empty every time the modal opens
    useEffect(() => {
        if (!show) return;
        setCode("");
        setErrorMsgId(null);
    }, [show]);

    const trimmedCode: string = code.trim();
    const canLink: boolean = trimmedCode.length === EMPLOYEE_CODE_LENGTH && !isPending;

    const linkAndPrint = (e?: FormEvent) => {
        e?.preventDefault();
        if (!canLink) return;
        setErrorMsgId(null);
        print(trimmedCode);
    };

    return (
        <Modal
            show={show}
            onClose={() => setShow(false)}
            title={intl.formatMessage({id: "printDeliveryReceipt"})}
            closeButtonTitle={intl.formatMessage({id: "cancel"})}
            disabled={isPending}
            loading={isPending}
            position={"center-bottom"}
            breakpoints={{md: {position: "center"}}}
            actionRow={
                <ActionRow>
                    <ActionRowButton
                        color={"secondary"}
                        disabled={isPending}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    print(null);
                                },
                            },
                        }}
                    >
                        <FormattedMessage id={"printWithoutLinking"}/>
                    </ActionRowButton>
                    <ActionRowButton
                        disabled={!canLink}
                        loading={isPending}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => linkAndPrint(e),
                            },
                        }}
                    >
                        <FormattedMessage id={"linkAndPrint"}/>
                    </ActionRowButton>
                </ActionRow>
            }
        >
            <form className={"flex flex-col gap-4 mt-4"} onSubmit={linkAndPrint}>
                <p>
                    <FormattedMessage id={"linkCourierDescription"}/>
                </p>

                {
                    order.courier !== null &&
                    <Alert
                        type={"primary"}
                        title={
                            <FormattedMessage
                                id={"currentCourier"}
                                values={{name: `${order.courier.firstName} ${order.courier.lastName}`}}
                            />
                        }
                    />
                }

                <TextField
                    type={"text"}
                    inputMode={"numeric"}
                    autoComplete={"off"}
                    autoFocus={true}
                    maxLength={EMPLOYEE_CODE_LENGTH}
                    label={
                        <InputLabel>
                            <FormattedMessage id={"employeeCode"}/>
                        </InputLabel>
                    }
                    placeholder={"0000"}
                    value={code}
                    onChange={e => {
                        setCode(e.target.value);
                        setErrorMsgId(null);
                    }}
                    disabled={isPending}
                    error={errorMsgId === null ? undefined : intl.formatMessage({id: errorMsgId})}
                />
            </form>
        </Modal>
    );
};

export default CourierPrintModal;